import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import service from '../../services/api/workAllocationService';
import Button from '../common/Button';

const storageKey = (actorId, reportId) => `tempus.local-employer-review.v2:${actorId}:${reportId}`;
const definitiveRejections = new Set(['LOCAL_EMPLOYER_REVIEW_STALE', 'LOCAL_EMPLOYER_REVIEW_INVALID',
  'LOCAL_EMPLOYMENT_SOURCE_REVIEW_REQUIRED', 'WORK_FINANCIAL_HISTORY_PROTECTED', 'LOCAL_EMPLOYER_ALREADY_REVIEWED',
  'WORK_EMPLOYER_AUTHORITY_CONFLICT', 'WORK_PLACEMENT_MISMATCH', 'WORK_ALREADY_ALLOCATED']);
const reasons = {
  LOCAL_EMPLOYMENT_SOURCE_REVIEW_REQUIRED: 'CRM or another source owns this employment. Ask that source owner to review its placement and legal-employer evidence.',
  LOCAL_EMPLOYER_ALREADY_REVIEWED: 'Employer authority has already been reviewed. Preview allocation using the accepted legal employer.',
  WORK_EMPLOYER_AUTHORITY_CONFLICT: 'Accepted employer evidence conflicts. Ask the employment source owner to resolve the evidence before allocation.',
  WORK_EMPLOYER_EVIDENCE_REQUIRED: 'Legal-employer evidence is missing. A payroll administrator can review a local employment; source-owned employments need review in their source system.',
  WORK_FINANCIAL_HISTORY_PROTECTED: 'A payroll administrator must review the retained payroll, export or invoice evidence. This screen cannot change frozen history. A rate correction does not resolve an unresolved-work allocation.',
  WORK_PLACEMENT_MISMATCH: 'Select the actual employment covering this worker, customer and work date.',
  WORK_ALREADY_ALLOCATED: 'The work is already allocated. Refresh the allocation list to inspect the accepted result.',
};

// Persist before sending. A reload or a lost response must recover the same
// reviewed command, without silently selecting newer evidence or authority.
function readPending(actorId, reportId) {
  const raw = sessionStorage.getItem(storageKey(actorId, reportId));
  if (!raw) return null;
  const value = JSON.parse(raw);
  const fields = ['commandId', 'placementId', 'employerCvr', 'expectedEmploymentRevision', 'expectedMappingRevision', 'reviewHash', 'evidenceRef', 'reason'];
  if (value.schema !== 'local-employer-review-pending/v2' || value.actorUserId !== actorId || value.reportedWorkId !== reportId
    || !value.command || Object.keys(value.command).sort().join() !== fields.sort().join()
    || fields.some(field => typeof value.command[field] !== 'string' || !value.command[field])) {
    throw new Error('The retained employer review cannot be read. Ask support to inspect this browser record before submitting another review.');
  }
  return value.command;
}

export default function EmployerAuthorityReview({ actorId, reportId, placementId, employerCvr, busy: parentBusy, onPending, onAccepted }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [evidenceRef, setEvidenceRef] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  // The parent mounts a fresh keyed review with allocation controls locked.
  // Read storage before the first render so recovery never briefly enables
  // different placement/evidence inputs while the retained command loads.
  const [retained] = useState(() => {
    try { return { command: readPending(actorId, reportId) }; }
    catch (e) { return { command: null, error: e.message }; }
  });
  const [pending, setPending] = useState(retained.command);
  const [rejected, setRejected] = useState(false);
  const blockedStorage = Boolean(retained.error);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(retained.error || '');
  const [receipt, setReceipt] = useState(null);
  useEffect(() => { onPending(Boolean(pending) || blockedStorage || busy); }, [pending, blockedStorage, busy, onPending]);
  useEffect(() => { setPreview(null); setConfirmed(false); }, [placementId, employerCvr]);
  const message = e => e.response?.data?.error?.message || e.message;
  const loadPreview = async () => {
    setBusy(true); setError(''); setPreview(null); setConfirmed(false);
    try {
      const result = await service.employerReviewPreview(reportId, { placementId, employerCvr });
      if (result.contractVersion !== 'local-employer-review/v1' || result.reportedWorkId !== reportId
        || result.placement?.id !== placementId || result.employerCvr !== employerCvr || result.authorityOnly !== true) {
        throw new Error('Employer review did not match the selected employment. Reload the current facts before continuing.');
      }
      setPreview(result);
    } catch (e) { setError(message(e)); } finally { setBusy(false); }
  };
  const submit = async () => {
    if (!pending && (!preview?.permitted || preview.placement.id !== placementId || preview.employerCvr !== employerCvr
      || !confirmed || !/^[A-Za-z0-9._:/-]{1,160}$/.test(evidenceRef.trim()) || !reason.trim())) return;
    setBusy(true); setError(''); setRejected(false);
    const body = pending || { commandId: window.crypto.randomUUID(), placementId: preview.placement.id,
      employerCvr: preview.employerCvr, expectedEmploymentRevision: preview.expectedEmploymentRevision,
      expectedMappingRevision: preview.expectedMappingRevision, reviewHash: preview.reviewHash,
      evidenceRef: evidenceRef.trim(), reason: reason.trim() };
    let sent = false;
    try {
      if (!pending) {
        sessionStorage.setItem(storageKey(actorId, reportId), JSON.stringify({ schema: 'local-employer-review-pending/v2', actorUserId: actorId, reportedWorkId: reportId, command: body }));
        setPending(body);
      }
      sent = true;
      const result = await service.reviewEmployer(reportId, body);
      if (result.contractVersion !== 'local-employer-review/v1'
        || !/^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(result.reviewId || '')
        || !/^[a-f0-9]{64}$/.test(result.contentHash || '')
        || String(result.actorUserId) !== actorId || result.outcome !== 'employer_reviewed' || result.authorityOnly !== true || result.commandId !== body.commandId
        || result.reportedWorkId !== reportId || result.placementId !== body.placementId || result.employerCvr !== body.employerCvr) {
        throw new Error('The response did not identify the retained review. Recover this same command before making another decision.');
      }
      sessionStorage.removeItem(storageKey(actorId, reportId));
      setReceipt(result); setPending(null); setPreview(null); setConfirmed(false);
      await onAccepted(body);
    } catch (e) {
      const status = e.response?.status;
      // Auth, concurrent transactions, conflicts, transport errors and 5xx can
      // never authorize forgetting a command that might already have committed.
      setRejected(sent && status >= 400 && status < 500 && definitiveRejections.has(e.response?.data?.error?.code));
      setError(message(e));
    } finally { setBusy(false); }
  };
  const discardRejected = () => {
    try {
      sessionStorage.removeItem(storageKey(actorId, reportId)); setPending(null); setRejected(false);
      setPreview(null); setConfirmed(false); setError('');
    } catch (e) { setError(message(e)); }
  };
  const disabled = parentBusy || busy || blockedStorage;
  return <section aria-label={t('workAllocation.employerReviewTitle', { defaultValue: 'Local employment employer review' })}>
    <h3>{t('workAllocation.employerReviewTitle', { defaultValue: 'Local employment employer review' })}</h3>
    <p>{t('workAllocation.employerReviewHelp', { defaultValue: 'For a local employment, a payroll administrator can record the legal employer from reviewed documentary evidence. This records employer authority only. Allocation, approval of hours and payroll remain separate actions.' })}</p>
    {error && <p role="alert">{error}</p>}
    {receipt && <p role="status">{t('workAllocation.employerReviewed', { defaultValue: 'Legal employer recorded. Review the allocation preview before allocating work.' })} {receipt.placementId} · {receipt.employerCvr}</p>}
    {pending ? <div>
      <p>{t('workAllocation.employerPending', { defaultValue: 'A submitted employer review is retained. Recover its exact result before changing the reviewed facts.' })}</p>
      <dl><dt>{t('workAllocation.placement', { defaultValue: 'Placement for this work date' })}</dt><dd>{pending.placementId}</dd>
        <dt>{t('workAllocation.employer', { defaultValue: 'Legal employer CVR' })}</dt><dd>{pending.employerCvr}</dd>
        <dt>{t('workAllocation.evidenceRef', { defaultValue: 'Evidence reference' })}</dt><dd>{pending.evidenceRef}</dd>
        <dt>{t('workAllocation.employerReason', { defaultValue: 'Employer review reason' })}</dt><dd>{pending.reason}</dd>
        <dt>{t('workAllocation.reviewCommand', { defaultValue: 'Review command' })}</dt><dd>{pending.commandId}</dd></dl>
      {rejected ? <>
        <p>{t('workAllocation.employerRejected', { defaultValue: 'The server rejected this review without creating employer authority. Review the current facts before submitting a new decision.' })}</p>
        <Button disabled={disabled} onClick={discardRejected}>{t('workAllocation.employerNewPreview', { defaultValue: 'Discard rejected command and review current facts' })}</Button>
      </> : <Button disabled={disabled} onClick={submit}>{t('workAllocation.employerRecover', { defaultValue: 'Recover exact employer review' })}</Button>}
    </div> : <fieldset disabled={disabled}>
      <legend>{t('workAllocation.employerEvidence', { defaultValue: 'Review legal-employer evidence' })}</legend>
      <Button onClick={loadPreview} disabled={!placementId || !/^\d{8}$/.test(employerCvr)}>{t('workAllocation.employerPreview', { defaultValue: 'Preview employer review' })}</Button>
      {preview && <>
        <p>{preview.placement.id} · #{preview.placement.employeeId} · #{preview.placement.customerId} · {preview.placement.startDate} – {preview.placement.endDate || '…'} · {preview.employerCvr}</p>
        {preview.blockers.length > 0 && <ul>{preview.blockers.map(code => <li key={code}>{t(`workAllocation.reasons.${code}`, {
          defaultValue: reasons[code] || 'A payroll administrator must review the current employment evidence before recording employer authority.',
        })}</li>)}</ul>}
        <label>{t('workAllocation.evidenceRef', { defaultValue: 'Evidence reference' })}
          <input value={evidenceRef} maxLength={160} onChange={e => { setEvidenceRef(e.target.value); setConfirmed(false); }} /></label>
        <p>{t('workAllocation.evidenceRefHelp', { defaultValue: 'Use an opaque document or case reference with letters, numbers, dots, underscores, colons, slashes or hyphens. Do not enter document contents or personal data.' })}</p>
        <label>{t('workAllocation.employerReason', { defaultValue: 'Employer review reason' })}
          <textarea value={reason} maxLength={1000} onChange={e => { setReason(e.target.value); setConfirmed(false); }} /></label>
        <label><input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
          {t('workAllocation.employerConfirmEvidence', { defaultValue: 'I reviewed the evidence for this exact employment and legal employer.' })}</label>
        <Button onClick={submit} disabled={!preview.permitted || preview.placement.id !== placementId || preview.employerCvr !== employerCvr
          || !confirmed || !/^[A-Za-z0-9._:/-]{1,160}$/.test(evidenceRef.trim()) || !reason.trim()}>
          {t('workAllocation.employerConfirm', { defaultValue: 'Confirm reviewed legal employer' })}</Button>
      </>}
    </fieldset>}
  </section>;
}
